import { createSlice, isRejected } from "@reduxjs/toolkit";
import { fetchPlansAsync } from "components/common/plans/plans.slice";
import { addToMyPlansAsync } from "components/features/MyPlanDetails/MyPlanDetails.slice";

import { editPreferredEmail, fetchAllowedRoles } from "components/app/App.slice";
import {
  assignMultiplePlans,
  fetchAboveProperties,
  fetchAssigneeDetailsByHtlIdsAsync,
  fetchAssigneeDetailsByInnCodesAsync,
  fetchBookmarkPropertyOrPlan,
  fetchMembers,
  fetchMultiTargetDetails,
  fetchMyPlansAsync,
  fetchPropertyInfo,
  fetchPropertySnapshot,
  fetchUnImplementedProperties,
  updateBookmarkPropertyOrPlan
} from "components/features/AboveProperty/AboveProperty.slice";
import {
  fetchCardsOverviewReportAsync,
  fetchDashboardPlansAsync,
  fetchDashboardReportAsync,
  fetchPlansOverviewReportAsync
} from "components/features/dashboard/Dashboard.slice";
import { fetchFunctionalAreaAsync } from "components/features/library/libraryHall/Hall.slice";
import { fetchSuggestedPlansIdAsync } from "components/features/library/Suggested/Suggested.slice";
import {
  copyActionAsync,
  createSectionAsync,
  deleteActionCardAsync,
  deleteSectionAsync,
  editSectionAsync,
  fetchMyPlanDetailAsync,
  markPlanCompleteAsync,
  patchAddInviteeAsync,
  patchReorderSectionsAsync,
  patchUpdateOwnershipAsync,
  removeMemberBulkActionAsync,
  updateMyPlanCardDetailAsync,
  updatePlanAsync
} from "components/features/MyPlanDetails/MyPlanDetails.slice";
import { deleteMyPlansAsync } from "components/features/MyPlans/myplans.slice";
import { deleteNotifications } from "components/features/Notifications/Notifications.slice";
import { fetchOnboardingQueriesCheck } from "components/features/Onboarding/Onboarding.slice";
import { createCommentAsync, fetchPlanDetailAsync } from "components/features/planDetails/PlanDetail.slice";
import { uploadFileAsync } from "components/features/Recommendations/Recommendations.slice";
import {
  assignPlanstoInncode,
  deleteMapping,
  deleteMyList,
  mapUserToInncode,
  updateInncodeUploadList,
  uploadMyList
} from "components/features/SME/home/sme.slice";
import { markCompleteSuccessMessage, messages } from "utils/CommonConstants";
import { responseStatusTypes, responseTypes } from "utils/Types";
const isARejectedAction = isRejected(
  editPreferredEmail,
  fetchAllowedRoles,
  deleteNotifications,
  fetchOnboardingQueriesCheck,
  fetchDashboardReportAsync,
  fetchPlansOverviewReportAsync,
  fetchCardsOverviewReportAsync,
  fetchSuggestedPlansIdAsync,
  fetchPlansAsync,
  fetchDashboardPlansAsync,
  fetchFunctionalAreaAsync,
  fetchPlanDetailAsync,
  fetchMyPlanDetailAsync,
  fetchMembers,
  assignMultiplePlans,
  fetchAssigneeDetailsByHtlIdsAsync,
  fetchAssigneeDetailsByInnCodesAsync,
  fetchPropertySnapshot,
  fetchAboveProperties,
  fetchBookmarkPropertyOrPlan,
  updateBookmarkPropertyOrPlan,
  fetchPropertyInfo,
  fetchMultiTargetDetails,
  fetchUnImplementedProperties
);
const initialState: any = {
  fetchMyPlans: {},
  addToMyPlans: {},
  copyAction: {},
  updateMyPlanCardDetail: {},
  createSection: {},
  editSection: {},
  deleteSection: {},
  deletePlan: {},
  reorderSections: {},
  deleteActionCard: {},
  deleteMultipleActionCards: {},
  updatePlan: {},
  addInvitee: {},
  removeInvitee: {},
  addComment: {},
  updateOwner: {},
  recommendationsFileUpload: {},
  assignPlan: {},
  apiFailure: {},
  smeDeleteMapping: {},
  smeMapUserToInncode: {},
  smeUploadMyList: {},
  smeDeleteMyList: {},
  markComplete: {}
};
const IsServerError = (error: number) => {
  if ((error >= responseTypes.internal_error && error < responseTypes.other_error) || error === responseTypes.forbidden)
    return true;
  return false;
};
export const FormSlice = createSlice({
  name: "forms",
  initialState,
  reducers: {
    clearFormState: (state, action) => {
      state[action.payload] = {};
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMyPlansAsync.rejected, (state, action: any) => {
        state.fetchMyPlans.status = responseStatusTypes.error;
        state.fetchMyPlans.details = action.payload.errors;
      })
      .addCase(assignPlanstoInncode.rejected, (state, action: any) => {
        state.assignPlan.status = responseStatusTypes.error;
        state.assignPlan.details = action.payload.errors;
      })
      .addCase(addToMyPlansAsync.fulfilled, (state) => {
        state.addToMyPlans.status = responseStatusTypes.success;
      })
      .addCase(addToMyPlansAsync.rejected, (state, action: any) => {
        state.addToMyPlans.status = responseStatusTypes.error;
        state.addToMyPlans.details = action.payload.errors;
      })
      .addCase(copyActionAsync.fulfilled, (state) => {
        state.copyAction.status = responseStatusTypes.success;
      })
      .addCase(copyActionAsync.rejected, (state, action: any) => {
        state.copyAction.status = responseStatusTypes.error;
        state.copyAction.details = action.payload.errors;
      })
      .addCase(updateMyPlanCardDetailAsync.fulfilled, (state) => {
        state.updateMyPlanCardDetail.status = responseStatusTypes.success;
      })
      .addCase(updateMyPlanCardDetailAsync.rejected, (state, action: any) => {
        state.updateMyPlanCardDetail.status = responseStatusTypes.error;
        state.updateMyPlanCardDetail.details = action.payload.errors;
      })
      .addCase(createSectionAsync.fulfilled, (state) => {
        state.createSection.status = responseStatusTypes.success;
      })
      .addCase(createSectionAsync.rejected, (state, action: any) => {
        state.createSection.status = responseStatusTypes.error;
        state.createSection.details = action.payload.errors;
      })
      .addCase(editSectionAsync.fulfilled, (state) => {
        state.editSection.status = responseStatusTypes.success;
      })
      .addCase(editSectionAsync.rejected, (state, action: any) => {
        state.editSection.status = responseStatusTypes.error;
        state.editSection.details = action.payload.errors;
      })
      .addCase(patchUpdateOwnershipAsync.fulfilled, (state, action: any) => {
        state.updateOwner.memberAction = action.meta?.arg?.memberAction;
        state.updateOwner.status = responseStatusTypes.success;
      })
      .addCase(patchUpdateOwnershipAsync.rejected, (state, action: any) => {
        state.updateOwner.status = responseStatusTypes.error;
        state.updateOwner.details = action.payload.errors;
      })
      .addCase(deleteSectionAsync.fulfilled, (state) => {
        state.deleteSection.status = responseStatusTypes.success;
      })
      .addCase(deleteSectionAsync.rejected, (state, action: any) => {
        state.deleteSection.status = responseStatusTypes.error;
        state.deleteSection.details = action.payload.errors;
      })
      .addCase(deleteMyPlansAsync.fulfilled, (state) => {
        state.deletePlan.status = responseStatusTypes.success;
      })
      .addCase(deleteMyPlansAsync.rejected, (state, action: any) => {
        state.deletePlan.status = responseStatusTypes.error;
        state.deletePlan.details = action.payload.errors;
      })
      .addCase(patchReorderSectionsAsync.fulfilled, (state) => {
        state.reorderSections.status = responseStatusTypes.success;
      })
      .addCase(patchReorderSectionsAsync.rejected, (state, action: any) => {
        state.reorderSections.status = responseStatusTypes.error;
        state.reorderSections.details = action.payload.errors;
      })
      .addCase(deleteActionCardAsync.fulfilled, (state, action: any) => {
        const idsArray = action.payload.data.cardId;

        if (idsArray?.length > 1 && action.payload.cardEditable) {
          state.deleteMultipleActionCards.status = responseStatusTypes.success;
        } else {
          state.deleteActionCard.status = responseStatusTypes.success;
        }
      })
      .addCase(deleteActionCardAsync.rejected, (state, action: any) => {
        state.deleteActionCard.status = responseStatusTypes.error;
        state.deleteActionCard.details = action.payload.errors;
      })
      .addCase(updatePlanAsync.fulfilled, (state) => {
        state.updatePlan.status = responseStatusTypes.success;
      })
      .addCase(updatePlanAsync.rejected, (state, action: any) => {
        state.updatePlan.status = responseStatusTypes.error;
        state.updatePlan.details = action.payload.errors;
      })
      .addCase(patchAddInviteeAsync.fulfilled, (state) => {
        state.addInvitee.status = responseStatusTypes.success;
      })
      .addCase(patchAddInviteeAsync.rejected, (state, action: any) => {
        state.addInvitee.status = responseStatusTypes.error;
        state.addInvitee.details = action.payload.errors;
      })
      .addCase(removeMemberBulkActionAsync.fulfilled, (state) => {
        state.removeInvitee.status = responseStatusTypes.success;
      })
      .addCase(removeMemberBulkActionAsync.rejected, (state, action: any) => {
        state.removeInvitee.status = responseStatusTypes.error;
        state.removeInvitee.details = action.payload.errors;
      })
      .addCase(createCommentAsync.rejected, (state, action: any) => {
        state.addComment.status = responseStatusTypes.error;
        state.addComment.details = action.payload.errors;
      })
      .addCase(uploadFileAsync.fulfilled, (state, action) => {
        state.recommendationsFileUpload.status = responseStatusTypes.success;
      })
      .addCase(uploadFileAsync.rejected, (state, action: any) => {
        state.recommendationsFileUpload.status = responseStatusTypes.error;
        state.recommendationsFileUpload.details = action.payload;
      })
      .addCase(deleteMyList.fulfilled, (state, action: any) => {
        state.smeDeleteMyList.status = responseStatusTypes.success;
      })

      .addCase(mapUserToInncode.fulfilled, (state, action: any) => {
        state.smeMapUserToInncode.status = responseStatusTypes.success;
      })
      .addCase(deleteMapping.fulfilled, (state, action: any) => {
        state.smeDeleteMapping.status = responseStatusTypes.success;
      })
      .addCase(deleteMyList.rejected, (state, action: any) => {
        state.smeDeleteMyList.status = responseStatusTypes.error;
        state.smeDeleteMyList.details = action.payload?.message;
      })
      .addCase(uploadMyList.fulfilled, (state, action: any) => {
        if (!action?.payload?.invalidMappings?.length) {
          state.smeUploadMyList.status = responseStatusTypes.success;
          state.smeUploadMyList.details = messages.listUploadSuccess;
        }
      })
      .addCase(uploadMyList.rejected, (state, action: any) => {
        state.smeUploadMyList.status = responseStatusTypes.error;
        state.smeUploadMyList.details = action.payload.errors;
      })
      .addCase(markPlanCompleteAsync.fulfilled, (state, action: any) => {
        if (action?.payload?.totalCards !== action?.payload?.cardsCompleted) {
          state.markComplete.status = responseStatusTypes.success;
          state.markComplete.details = markCompleteSuccessMessage[action?.meta?.arg?.type];
        }
      })
      .addCase(markPlanCompleteAsync.rejected, (state, action: any) => {
        state.markComplete.status = responseStatusTypes.error;
        state.markComplete.details = action.payload.errors;
      })
      .addCase(updateInncodeUploadList.fulfilled, (state, action: any) => {
        state.smeUploadMyList.status = responseStatusTypes.success;
        state.smeUploadMyList.details = messages.listUploadSuccess;
      })
      .addCase(updateInncodeUploadList.rejected, (state, action: any) => {
        state.smeUploadMyList.status = responseStatusTypes.error;
        state.smeUploadMyList.details = action.payload.errors;
      })
      .addCase(mapUserToInncode.rejected, (state, action: any) => {
        state.smeMapUserToInncode.status = responseStatusTypes.error;
        state.smeMapUserToInncode.details = action.payload.errors;
      })
      .addCase(deleteMapping.rejected, (state, action: any) => {
        state.smeDeleteMapping.status = responseStatusTypes.error;
        state.smeDeleteMapping.details = action.payload?.message;
      })
      .addMatcher(isARejectedAction, (state, action: any) => {
        if (IsServerError(action?.payload?.status)) {
          state.apiFailure.status = responseStatusTypes.error;
          state.apiFailure.details = action?.payload?.data?.errors;
        }
      });
  }
});

export const { clearFormState } = FormSlice.actions;
export default FormSlice.reducer;
